import { useState } from 'react';

type DocSection = 'overview' | 'auth' | 'tokens' | 'assets' | 'liquidity' | 'websocket' | 'errors';

export function Docs() {
  const [activeSection, setActiveSection] = useState<DocSection>('overview');

  const sections: { id: DocSection; title: string }[] = [
    { id: 'overview', title: 'Overview' },
    { id: 'auth', title: 'Authentication' },
    { id: 'tokens', title: 'Tokens' },
    { id: 'assets', title: 'Assets' },
    { id: 'liquidity', title: 'Liquidity' },
    { id: 'websocket', title: 'WebSocket' },
    { id: 'errors', title: 'Error Handling' },
  ];

  return (
    <div className="docs-page">
      <div className="docs-container">
        <nav className="docs-sidebar">
          <h3>API Documentation</h3>
          <ul>
            {sections.map(section => (
              <li key={section.id}>
                <button
                  className={activeSection === section.id ? 'active' : ''}
                  onClick={() => setActiveSection(section.id)}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="docs-content">
          {activeSection === 'overview' && <OverviewSection />}
          {activeSection === 'auth' && <AuthSection />}
          {activeSection === 'tokens' && <TokensSection />}
          {activeSection === 'assets' && <AssetsSection />}
          {activeSection === 'liquidity' && <LiquiditySection />}
          {activeSection === 'websocket' && <WebSocketSection />}
          {activeSection === 'errors' && <ErrorsSection />}
        </main>
      </div>
    </div>
  );
}

function OverviewSection() {
  return (
    <section>
      <h1>DexScan API Overview</h1>
      <p>
        The DexScan API provides real-time access to token data, prices, and analytics
        on Klever Chain. Access DEX trading data, historical candles, and live price updates.
      </p>

      <h2>Base URL</h2>
      <CodeBlock code="https://api.dexscan.me" />

      <h2>Features</h2>
      <ul className="feature-list">
        <li>Real-time token prices and market data</li>
        <li>Historical OHLCV candle data</li>
        <li>Token metadata and statistics</li>
        <li>WebSocket for live price updates</li>
        <li>Cross-DEX aggregated data</li>
      </ul>

      <h2>Rate Limits</h2>
      <p>
        Rate limits depend on your subscription plan. Limits are enforced per minute.
        Check your dashboard for your current plan's limits.
      </p>
      <table className="docs-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Requests/min</th>
            <th>Daily Limit</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Basic</td>
            <td>10</td>
            <td>1,500</td>
          </tr>
          <tr>
            <td>Pro</td>
            <td>30</td>
            <td>Unlimited</td>
          </tr>
          <tr>
            <td>Enterprise</td>
            <td>1,000</td>
            <td>Unlimited</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function AuthSection() {
  return (
    <section>
      <h1>Authentication</h1>
      <p>
        All API requests require authentication using an API key. Include your API key
        in the request header.
      </p>

      <h2>API Key Header</h2>
      <CodeBlock
        language="http"
        code={`X-API-Key: your_api_key_here`}
      />

      <h2>Example Request</h2>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/tokens/overview" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h2>Getting Your API Key</h2>
      <ol>
        <li>Connect your Klever wallet on this portal</li>
        <li>Select a subscription plan</li>
        <li>Complete payment</li>
        <li>Your API key will appear in your Dashboard</li>
      </ol>

      <div className="warning-box">
        <strong>Security Note:</strong> Keep your API key secret. Do not expose it in
        client-side code or public repositories.
      </div>
    </section>
  );
}

function TokensSection() {
  return (
    <section>
      <h1>Tokens Endpoints</h1>

      <h2>Get All Tokens</h2>
      <p>Returns a list of all tokens with current market data, sorted by volume.</p>
      <EndpointBox method="GET" path="/tokens/overview" />

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/tokens/overview" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": {
    "klv_price": 0.00148,
    "total_tvl": 1250000.50,
    "tokens": [
      {
        "ticker": "DVK-5H2R",
        "price": {
          "current": 0.00045678,
          "high_24h": 0.00048500,
          "low_24h": 0.00042100
        },
        "price_changes": {
          "change_30m": 0.5,
          "change_1h": 1.2,
          "change_4h": -2.1,
          "change_24h": 5.23
        },
        "volume": {
          "usd_24h": 125000.50,
          "tokens_24h": 273500000
        },
        "activity": {
          "txns_24h": 456,
          "makers_24h": 89
        },
        "liquidity": {
          "tvl_usd": 89000,
          "pools_count": 3
        },
        "mcap_usd": 4500000,
        "primary_dex": "KleverSwap"
      }
    ],
    "updated_at": "2026-01-23T15:30:00Z"
  }
}`}
      />
    </section>
  );
}

function AssetsSection() {
  return (
    <section>
      <h1>Asset Endpoints</h1>

      <h2>Get Asset Price</h2>
      <p>Returns the current USD price for a specific asset.</p>
      <EndpointBox method="GET" path="/asset/{ticker}" />

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/asset/DVK-5H2R" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": {
    "time": 1706018400000,
    "ticker": "DVK-5H2R",
    "price": "0.00045678"
  }
}`}
      />

      <h2>Get Full Asset Details</h2>
      <p>Returns comprehensive asset data including price, liquidity pools, TVL, and 24h volume.</p>
      <EndpointBox method="GET" path="/asset/{ticker}/full" />

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/asset/DVK-5H2R/full" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": {
    "time": 1706018400000,
    "ticker": "DVK-5H2R",
    "price": "0.00045678",
    "liquidity_usd": 89000.50,
    "tvl": 89000.50,
    "apr": null,
    "volume_24h": 125000.50,
    "pools": [
      {
        "dex": "KleverSwap",
        "pair": "DVK-5H2R/KLV",
        "reserve_token": 15000000.0,
        "reserve_klv": 6850.5,
        "tvl_usd": 45000.25,
        "volume_24h_usd": 62500.25
      },
      {
        "dex": "Swopus",
        "pair": "DVK-5H2R/KLV",
        "reserve_token": 12500000.0,
        "reserve_klv": 5700.2,
        "tvl_usd": 44000.25,
        "volume_24h_usd": 62500.25
      }
    ]
  }
}`}
      />

      <h2>Get Multiple Assets (Batch)</h2>
      <p>Returns price data for multiple assets in a single request. Supports pagination for large requests.</p>
      <EndpointBox method="GET" path="/assets/batch" />

      <h3>Parameters</h3>
      <table className="docs-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>tickers</code></td>
            <td>string</td>
            <td>Yes</td>
            <td>Comma-separated list of tickers</td>
          </tr>
          <tr>
            <td><code>page</code></td>
            <td>integer</td>
            <td>No</td>
            <td>Page number (default: 1)</td>
          </tr>
          <tr>
            <td><code>page_size</code></td>
            <td>integer</td>
            <td>No</td>
            <td>Results per page (default: 50, max: 100)</td>
          </tr>
        </tbody>
      </table>

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/assets/batch?tickers=DVK-5H2R,KFI,KUSD-2L8F" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": [
    {
      "time": 1706018400000,
      "ticker": "DVK-5H2R",
      "price": "0.00045678"
    },
    {
      "time": 1706018400000,
      "ticker": "KFI",
      "price": "12.50000000"
    },
    {
      "time": 1706018400000,
      "ticker": "KUSD-2L8F",
      "price": "1.00020000"
    }
  ],
  "pagination": {
    "page": 1,
    "page_size": 50,
    "total_items": 3,
    "total_pages": 1,
    "has_next": false,
    "has_prev": false
  }
}`}
      />

      <h2>Get Historical Prices (OHLCV)</h2>
      <p>Returns historical OHLCV (Open, High, Low, Close, Volume) candle data for charting and analysis.</p>
      <EndpointBox method="GET" path="/asset/{ticker}/history" />

      <h3>Parameters</h3>
      <table className="docs-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Type</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>ticker</code></td>
            <td>string</td>
            <td>Yes (path)</td>
            <td>Asset ticker (e.g., <code>DVK-5H2R</code>, <code>KLV</code>)</td>
          </tr>
          <tr>
            <td><code>interval</code></td>
            <td>string</td>
            <td>No</td>
            <td>Candle timeframe: <code>30m</code>, <code>1h</code>, <code>4h</code>, <code>1d</code> (default: <code>1h</code>)</td>
          </tr>
          <tr>
            <td><code>from</code></td>
            <td>integer</td>
            <td>No</td>
            <td>Start timestamp in Unix seconds (default: 7 days ago)</td>
          </tr>
          <tr>
            <td><code>to</code></td>
            <td>integer</td>
            <td>No</td>
            <td>End timestamp in Unix seconds (default: now)</td>
          </tr>
          <tr>
            <td><code>limit</code></td>
            <td>integer</td>
            <td>No</td>
            <td>Max candles to return (default: 500, max: 1000)</td>
          </tr>
        </tbody>
      </table>

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/asset/DVK-5H2R/history?interval=1h&limit=100" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": {
    "ticker": "DVK-5H2R",
    "interval": "1h",
    "candles": [
      {
        "time": 1706014800,
        "open": 0.00045123,
        "high": 0.00046500,
        "low": 0.00044800,
        "close": 0.00045678,
        "volume": 12500.50
      },
      {
        "time": 1706018400,
        "open": 0.00045678,
        "high": 0.00047200,
        "low": 0.00045100,
        "close": 0.00046890,
        "volume": 18750.25
      }
    ]
  }
}`}
      />

      <h3>Notes</h3>
      <ul className="feature-list">
        <li>Candles are sorted oldest to newest (ascending by time)</li>
        <li>Timestamps are in Unix seconds (not milliseconds)</li>
        <li>KLV is supported - historical data is aggregated from external price feeds</li>
        <li>Volume may be 0 for assets without DEX trading activity (e.g., KLV)</li>
      </ul>
    </section>
  );
}

function LiquiditySection() {
  return (
    <section>
      <h1>Liquidity Endpoints</h1>

      <h2>Get Liquidity Overview</h2>
      <p>Returns an overview of all liquidity pools across all DEXes.</p>
      <EndpointBox method="GET" path="/liquidity/overview" />

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/liquidity/overview" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": {
    "total_tvl_usd": 2500000.50,
    "total_volume_24h_usd": 450000.25,
    "pools": [
      {
        "dex": "KleverSwap",
        "token_a": "DVK-5H2R",
        "token_b": "KLV",
        "tvl_usd": 89000.50,
        "volume_24h_usd": 25000.25,
        "apr": 45.5
      },
      {
        "dex": "Swopus",
        "token_a": "KFI",
        "token_b": "KLV",
        "tvl_usd": 156000.75,
        "volume_24h_usd": 42000.50,
        "apr": 32.8
      }
    ],
    "updated_at": "2026-01-23T15:30:00Z"
  }
}`}
      />

      <h2>Get Pool Details</h2>
      <p>Returns detailed information for a specific liquidity pool.</p>
      <EndpointBox method="GET" path="/pool/{dex}/{token_a}/{token_b}" />

      <h3>Path Parameters</h3>
      <table className="docs-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>dex</code></td>
            <td>string</td>
            <td>DEX name: <code>kleverswap</code>, <code>swopus</code>, or <code>same</code></td>
          </tr>
          <tr>
            <td><code>token_a</code></td>
            <td>string</td>
            <td>First token ticker (e.g., <code>DVK-5H2R</code>)</td>
          </tr>
          <tr>
            <td><code>token_b</code></td>
            <td>string</td>
            <td>Second token ticker (e.g., <code>KLV</code>)</td>
          </tr>
        </tbody>
      </table>

      <h3>Example Request</h3>
      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.dexscan.me/pool/swopus/DVK-5H2R/KLV" \\
  -H "X-API-Key: dxs_your_api_key_here"`}
      />

      <h3>Example Response</h3>
      <CodeBlock
        language="json"
        code={`{
  "code": "successful",
  "data": {
    "dex": "Swopus",
    "token_a": "DVK-5H2R",
    "token_b": "KLV",
    "reserve_a": 15000000.0,
    "reserve_b": 6850.5,
    "tvl_usd": 89000.50,
    "volume_24h_usd": 25000.25,
    "trade_count_24h": 156,
    "price_token_a": 0.00045678,
    "price_ratio": 2189.78,
    "updated_at": "2026-01-23T15:30:00Z"
  }
}`}
      />
    </section>
  );
}

function WebSocketSection() {
  return (
    <section>
      <h1>WebSocket API</h1>
      <p className="plan-note">
        WebSocket access is available on <strong>Pro</strong> and <strong>Enterprise</strong> plans.
      </p>

      <h2>Connection</h2>
      <p>Connect to the WebSocket endpoint with your API key:</p>
      <CodeBlock
        code="wss://api.dexscan.me/ws?api_key=dxs_your_api_key_here"
      />

      <h2>Subscribe to Price Updates</h2>
      <p>Send a subscription message to receive real-time price updates:</p>
      <CodeBlock
        language="json"
        code={`{
  "action": "subscribe",
  "channel": "prices",
  "tickers": ["DVK-5H2R", "KLV"]
}`}
      />

      <h2>Price Update Message</h2>
      <p>You'll receive messages when prices update:</p>
      <CodeBlock
        language="json"
        code={`{
  "channel": "prices",
  "data": {
    "ticker": "DVK-5H2R",
    "price_usd": 0.00045678,
    "price_klv": 0.0234,
    "change_24h_percent": 5.23,
    "volume_24h_usd": 125000.50,
    "timestamp": "2026-01-23T15:30:00Z"
  }
}`}
      />

      <h2>JavaScript Example</h2>
      <CodeBlock
        language="javascript"
        code={`const ws = new WebSocket('wss://api.dexscan.me/ws?api_key=dxs_your_api_key_here');

ws.onopen = () => {
  console.log('Connected to DexScan WebSocket');

  // Subscribe to price updates
  ws.send(JSON.stringify({
    action: 'subscribe',
    channel: 'prices',
    tickers: ['DVK-5H2R', 'KLV']
  }));
};

ws.onmessage = (event) => {
  const data = JSON.parse(event.data);
  console.log('Price update:', data);
};

ws.onerror = (error) => {
  console.error('WebSocket error:', error);
};

ws.onclose = () => {
  console.log('Disconnected from WebSocket');
};`}
      />

      <h2>Unsubscribe</h2>
      <CodeBlock
        language="json"
        code={`{
  "action": "unsubscribe",
  "channel": "prices",
  "tickers": ["DVK-5H2R"]
}`}
      />
    </section>
  );
}

function ErrorsSection() {
  return (
    <section>
      <h1>Error Handling</h1>
      <p>The API uses standard HTTP status codes and returns error details in JSON format.</p>

      <h2>Error Response Format</h2>
      <CodeBlock
        language="json"
        code={`{
  "code": "error",
  "message": "Description of the error"
}`}
      />

      <h2>HTTP Status Codes</h2>
      <table className="docs-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Meaning</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>200</code></td>
            <td>OK</td>
            <td>Request successful</td>
          </tr>
          <tr>
            <td><code>400</code></td>
            <td>Bad Request</td>
            <td>Invalid parameters or malformed request</td>
          </tr>
          <tr>
            <td><code>401</code></td>
            <td>Unauthorized</td>
            <td>Missing or invalid API key</td>
          </tr>
          <tr>
            <td><code>403</code></td>
            <td>Forbidden</td>
            <td>API key doesn't have access to this endpoint</td>
          </tr>
          <tr>
            <td><code>404</code></td>
            <td>Not Found</td>
            <td>Resource not found (e.g., invalid ticker)</td>
          </tr>
          <tr>
            <td><code>429</code></td>
            <td>Too Many Requests</td>
            <td>Rate limit exceeded</td>
          </tr>
          <tr>
            <td><code>500</code></td>
            <td>Server Error</td>
            <td>Internal server error</td>
          </tr>
        </tbody>
      </table>

      <h2>Rate Limit Errors</h2>
      <p>When you exceed your rate limit, you'll receive a 429 response:</p>
      <CodeBlock
        language="json"
        code={`{
  "code": "error",
  "message": "Rate limit exceeded. Please wait before making more requests.",
  "retry_after": 60
}`}
      />

      <h2>Rate Limit Headers</h2>
      <p>Each response includes headers to help you track your usage:</p>
      <table className="docs-table">
        <thead>
          <tr>
            <th>Header</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>X-RateLimit-Limit</code></td>
            <td>Your plan's requests per minute limit</td>
          </tr>
          <tr>
            <td><code>X-RateLimit-Remaining</code></td>
            <td>Remaining requests in current window</td>
          </tr>
          <tr>
            <td><code>X-RateLimit-Reset</code></td>
            <td>Unix timestamp when the limit resets</td>
          </tr>
        </tbody>
      </table>

      <h2>Best Practices</h2>
      <ul className="feature-list">
        <li>Implement exponential backoff when receiving 429 errors</li>
        <li>Cache responses when possible to reduce API calls</li>
        <li>Use WebSocket for real-time data instead of polling</li>
        <li>Monitor your usage via the Dashboard</li>
      </ul>
    </section>
  );
}

// Helper Components
interface CodeBlockProps {
  code: string;
  language?: string;
}

function CodeBlock({ code, language }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // Session 1 (2026-05-25, closes PT-P2-7): guard against unavailable
    // clipboard API (HTTP origins, browsers with permission denied) +
    // catch promise rejection. Previously an unhandled rejection on a
    // non-HTTPS dev origin or denied permission produced a console
    // error + no feedback to the user.
    if (!navigator.clipboard?.writeText) {
      console.warn('Clipboard API unavailable (requires HTTPS or browser permission)');
      return;
    }
    navigator.clipboard.writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.warn('Clipboard write failed:', err);
      });
  };

  return (
    <div className="code-block">
      {language && <span className="code-language">{language}</span>}
      <button className="copy-btn" onClick={handleCopy}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <pre><code>{code}</code></pre>
    </div>
  );
}

interface EndpointBoxProps {
  method: string;
  path: string;
}

function EndpointBox({ method, path }: EndpointBoxProps) {
  return (
    <div className="endpoint-box">
      <span className={`method ${method.toLowerCase()}`}>{method}</span>
      <code>{path}</code>
    </div>
  );
}
